import { useState } from 'react';
import {
  useEnding, useRecordDeath, useRecordDischarge,
  type Ending, type DeathAction,
} from '../api/lifecycle.ts';
import type { FacilityOption } from '../api/places.ts';
import { FacilityPicker } from './PlacePicker.tsx';
import { ApiError } from '../api/client.ts';
import { formatDate } from '../dates.ts';

const field =
  'mt-1 w-full min-h-[48px] rounded-lg border border-neutral-300 bg-white px-3 ' +
  'text-base text-neutral-900';
const label = 'block text-sm font-semibold text-neutral-700';
const quiet =
  'min-h-[44px] rounded-lg border border-neutral-300 px-3 text-sm font-semibold ' +
  'text-neutral-800 hover:bg-neutral-50 active:bg-neutral-100';

function todayLocal(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * The two endings, behind a collapsed control.
 *
 * **Not beside Record a visit.** Recording a death or a discharge is
 * irreversible and starts statutory clocks, so it does not sit one misclick
 * away from the thing a case manager does forty times a week. Opening this is a
 * deliberate step, and it is an EXTRA one: it replaces none of the
 * confirmations below it — the date that is required and never defaulted, the
 * surname typed back, and the summary of what the act sets in motion, shown
 * before there is anything to press.
 *
 * Nothing here is fetched until the control is opened.
 *
 * Hidden where it makes no sense, and the server decides: no "has passed" for
 * someone already recorded as deceased, no "discharged" for someone already
 * discharged or deceased. When neither is possible the control says what is
 * already on record instead of offering an empty menu.
 */
export function AdvancedPanel({ clientId }: { clientId: string }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<'death' | 'discharge' | null>(null);
  const ending = useEnding(clientId, open);

  return (
    <section className="bg-white lg:rounded-lg lg:shadow">
      <button
        type="button"
        onClick={() => { setOpen((v) => !v); setForm(null); }}
        aria-expanded={open}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
      >
        <span className="text-xs font-bold tracking-[0.14em] text-neutral-500 uppercase">
          Advanced
        </span>
        <span className="text-sm text-neutral-500">
          {open ? 'Hide' : 'Ending this case'} {open ? '▴' : '▾'}
        </span>
      </button>

      {open && (
        <div className="border-t border-neutral-200 px-4 pt-3 pb-4">
          {ending.isLoading && <p className="text-neutral-500">Loading…</p>}
          {ending.error && (
            <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-900">
              {ending.error.message}
            </p>
          )}
          {ending.data && (
            <Body
              clientId={clientId}
              ending={ending.data}
              form={form}
              setForm={setForm}
              onDone={() => { setForm(null); setOpen(false); }}
            />
          )}
        </div>
      )}
    </section>
  );
}

function Body({
  clientId, ending, form, setForm, onDone,
}: {
  clientId: string;
  ending: Ending;
  form: 'death' | 'discharge' | null;
  setForm: (f: 'death' | 'discharge' | null) => void;
  onDone: () => void;
}) {
  // Already ended: say so, and offer nothing. `discharged_on` can be null for a
  // client discharged before the order became the record of it.
  if (ending.date_of_death) {
    return (
      <p className="text-neutral-800">
        Recorded as having passed on{' '}
        <span className="font-semibold">{formatDate(ending.date_of_death)}</span>.
      </p>
    );
  }
  if (ending.legal_status === 'Discharged') {
    return (
      <p className="text-neutral-800">
        Discharged
        {ending.discharged_on ? <> on <span className="font-semibold">{formatDate(ending.discharged_on)}</span></> : ''}.
      </p>
    );
  }

  if (!ending.may_record_death && !ending.may_record_discharge) {
    return (
      <p className="text-neutral-600">
        Ending a case is recorded by Legal, Compliance or the Director of Social Work.
      </p>
    );
  }

  if (form === 'death') {
    return <DeathForm clientId={clientId} ending={ending} onCancel={() => setForm(null)} onDone={onDone} />;
  }
  if (form === 'discharge') {
    return <DischargeForm clientId={clientId} ending={ending} onCancel={() => setForm(null)} onDone={onDone} />;
  }

  return (
    <>
      <p className="text-sm text-neutral-600">
        Both of these are permanent and start statutory clocks. You will see what each
        one sets in motion before anything is recorded.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {ending.may_record_death && (
          <button type="button" onClick={() => setForm('death')} className={quiet}>
            Client has passed
          </button>
        )}
        {ending.may_record_discharge && (
          <button type="button" onClick={() => setForm('discharge')} className={quiet}>
            Client has been discharged
          </button>
        )}
      </div>
    </>
  );
}

/** What it sets in motion, grouped the way the work is actually divided. */
function WhatFollows({ actions }: { actions: DeathAction[] }) {
  const byDept = actions.reduce<Record<string, DeathAction[]>>((acc, a) => {
    (acc[a.department] ??= []).push(a);
    return acc;
  }, {});
  return (
    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
      <p className="font-semibold text-neutral-900">
        This raises {actions.length} {actions.length === 1 ? 'action' : 'actions'} across{' '}
        {Object.keys(byDept).length} departments
      </p>
      <ul className="mt-1 space-y-1.5 text-sm">
        {Object.entries(byDept).map(([dept, list]) => (
          <li key={dept}>
            <span className="font-semibold text-neutral-700">{dept}</span>
            <ul className="mt-0.5 ml-3 space-y-0.5 text-neutral-700">
              {list.map((a) => (
                <li key={a.action_label}>
                  {a.action_label}
                  <span className="text-neutral-500">
                    {' '}· within {a.due_within_days} {a.due_within_days === 1 ? 'day' : 'days'}
                    {a.is_critical ? ' · critical' : ''}
                  </span>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DeathForm({
  clientId, ending, onCancel, onDone,
}: { clientId: string; ending: Ending; onCancel: () => void; onDone: () => void }) {
  const record = useRecordDeath(clientId);
  // NEVER DEFAULTED. A date of death pre-filled with today is a date somebody
  // did not choose, on a record that will be read in court.
  const [date, setDate] = useState('');
  const [surname, setSurname] = useState('');
  const [whereKind, setWhereKind] = useState<'facility' | 'other'>('facility');
  const [facility, setFacility] = useState<FacilityOption | null>(null);
  const [other, setOther] = useState('');
  const [cause, setCause] = useState('');
  const [informant, setInformant] = useState('');

  const ready = date.length === 10 && surname.trim() !== '';

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!ready) return;
        record.mutate(
          {
            confirm_last_name: surname.trim(),
            date_of_death: date,
            ...(whereKind === 'facility' && facility ? { place_facility_id: facility.id } : {}),
            ...(whereKind === 'other' && other.trim() ? { place_other: other.trim() } : {}),
            ...(cause.trim() ? { cause_of_death: cause.trim() } : {}),
            ...(informant.trim() ? { informant_name: informant.trim() } : {}),
          },
          { onSuccess: onDone },
        );
      }}
    >
      <h3 className="font-semibold text-neutral-900">Record that the client has passed</h3>

      <WhatFollows actions={ending.death_actions} />

      <label className={`${label} mt-3`} htmlFor="death-date">Date of death</label>
      <input id="death-date" type="date" className={field} value={date} max={todayLocal()}
             onChange={(e) => setDate(e.target.value)} />
      <p className="mt-1 text-xs text-neutral-500">Required, and deliberately not filled in.</p>

      <label className={`${label} mt-3`} htmlFor="death-where">Where</label>
      <div className="mt-1 flex gap-2">
        <button type="button" onClick={() => setWhereKind('facility')}
                className={`${quiet} ${whereKind === 'facility' ? '!bg-neutral-900 !text-white' : ''}`}>
          A facility
        </button>
        <button type="button" onClick={() => setWhereKind('other')}
                className={`${quiet} ${whereKind === 'other' ? '!bg-neutral-900 !text-white' : ''}`}>
          Somewhere else
        </button>
      </div>
      {whereKind === 'facility' ? (
        <FacilityPicker id="death-where" searchLabel="Which facility (optional)"
                        selected={facility} onSelect={setFacility} />
      ) : (
        <input id="death-where" className={field} value={other} maxLength={160}
               onChange={(e) => setOther(e.target.value)} placeholder="At home, in hospital…" />
      )}

      <label className={`${label} mt-3`} htmlFor="death-cause">Cause, if known</label>
      <input id="death-cause" className={field} value={cause} maxLength={160}
             onChange={(e) => setCause(e.target.value)} />

      <label className={`${label} mt-3`} htmlFor="death-informant">Who told us</label>
      <input id="death-informant" className={field} value={informant} maxLength={128}
             onChange={(e) => setInformant(e.target.value)} placeholder="Name" />

      {/* The last gate, and the one that costs something: typed, not ticked. */}
      <label className={`${label} mt-4`} htmlFor="death-surname">
        Type the client’s surname to confirm
      </label>
      <input id="death-surname" className={field} value={surname} autoComplete="off"
             onChange={(e) => setSurname(e.target.value)} placeholder="Surname" />
      <p className="mt-1 text-xs text-neutral-500">
        This cannot be undone from here.
      </p>

      <Failure error={record.error} />
      <div className="mt-3 flex gap-2">
        <button type="submit" disabled={!ready || record.isPending}
                className="min-h-[48px] flex-1 rounded-lg bg-neutral-900 px-4 font-semibold text-white
                           disabled:bg-neutral-300">
          {record.isPending ? 'Recording…' : 'Record that the client has passed'}
        </button>
        <button type="button" onClick={onCancel} className={quiet}>Cancel</button>
      </div>
    </form>
  );
}

function DischargeForm({
  clientId, ending, onCancel, onDone,
}: { clientId: string; ending: Ending; onCancel: () => void; onDone: () => void }) {
  const record = useRecordDischarge(clientId);
  const [signed, setSigned] = useState('');
  const [court, setCourt] = useState('');
  const [county, setCounty] = useState('');
  const [index, setIndex] = useState('');
  const [judge, setJudge] = useState('');

  const ready = signed.length === 10 && court.trim() !== '' && county.trim() !== '';

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!ready) return;
        record.mutate(
          {
            signed_date: signed,
            court_name: court.trim(),
            county: county.trim(),
            ...(index.trim() ? { court_index_number: index.trim() } : {}),
            ...(judge.trim() ? { judge_name: judge.trim() } : {}),
          },
          { onSuccess: onDone },
        );
      }}
    >
      <h3 className="font-semibold text-neutral-900">Record the Order Discharging Guardian</h3>
      <p className="mt-1 text-sm text-neutral-600">
        A discharge is an order, not a status. This records the order the judge signed;
        everything below follows from it.
      </p>

      <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
        <p className="font-semibold text-neutral-900">What this sets in motion</p>
        <ul className="mt-1 space-y-0.5 text-sm text-neutral-700">
          {ending.discharge_effects.map((e) => (
            <li key={e.effect}>
              <span className="font-semibold">{e.effect}</span>
              <span className="text-neutral-600"> — {e.detail}</span>
            </li>
          ))}
        </ul>
      </div>

      <label className={`${label} mt-3`} htmlFor="dis-signed">Date the order was signed</label>
      <input id="dis-signed" type="date" className={field} value={signed} max={todayLocal()}
             onChange={(e) => setSigned(e.target.value)} />
      <p className="mt-1 text-xs text-neutral-500">
        Required. The guardianship ends on this date, not today.
      </p>

      <label className={`${label} mt-3`} htmlFor="dis-court">Court</label>
      <input id="dis-court" className={field} value={court} maxLength={128}
             onChange={(e) => setCourt(e.target.value)} placeholder="Supreme Court" />

      <label className={`${label} mt-3`} htmlFor="dis-county">County</label>
      <input id="dis-county" className={field} value={county} maxLength={64}
             onChange={(e) => setCounty(e.target.value)} />

      <label className={`${label} mt-3`} htmlFor="dis-index">Index number</label>
      <input id="dis-index" className={field} value={index} maxLength={64}
             onChange={(e) => setIndex(e.target.value)} />

      <label className={`${label} mt-3`} htmlFor="dis-judge">Judge</label>
      <input id="dis-judge" className={field} value={judge} maxLength={128}
             onChange={(e) => setJudge(e.target.value)} />

      <Failure error={record.error} />
      <div className="mt-3 flex gap-2">
        <button type="submit" disabled={!ready || record.isPending}
                className="min-h-[48px] flex-1 rounded-lg bg-neutral-900 px-4 font-semibold text-white
                           disabled:bg-neutral-300">
          {record.isPending ? 'Recording…' : 'Record the discharge order'}
        </button>
        <button type="button" onClick={onCancel} className={quiet}>Cancel</button>
      </div>
    </form>
  );
}

function Failure({ error }: { error: Error | null }) {
  if (!error) return null;
  const api = error instanceof ApiError ? error : null;
  return (
    <p className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-900">
      {error.message}
      {api?.hint && <span className="block text-rose-700">{api.hint}</span>}
    </p>
  );
}
